import { IncomingMessage } from 'http';
import { mutate } from 'swr';

import { Collateral, CollateralPayload, Component } from 'models';
import { CollateralHelpers } from 'helpers/CollateralHelpers';

import type { Collateral as CollateralDocument } from '@/payload-types';

import { authHeaders, PayloadSDK } from './PayloadSDK';

const CollateralService = {
    mutate: {
        list: (companyId: string) => mutate(CollateralHelpers.swr.list(companyId)),
    },

    list: async (companyId: string) => {
        const { docs } = await PayloadSDK.find({
            collection: 'collateral',
            where: { company: { equals: companyId } },
            sort: 'createdAt',
            pagination: false,
            depth: 1,
        });

        return docs as unknown as Collateral[];
    },

    listByProduct: async (productId: string) => {
        const { docs } = await PayloadSDK.find({
            collection: 'collateral',
            where: { product: { equals: productId } },
            pagination: false,
            depth: 1,
        });

        return docs as unknown as Collateral[];
    },

    listProductFiles: async (companyId: string) => {
        const { docs } = await PayloadSDK.find({
            collection: 'components',
            where: {
                manufacturer: { equals: companyId },
                visibility: { equals: 'public' },
            },
            pagination: false,
            depth: 1,
            select: {
                name: true,
                productIdentifier: true,
                productSeries: true,
                type: true,
                files: true,
            },
        });

        return docs as unknown as Component[];
    },

    get: async (id: string, req?: IncomingMessage) => {
        const doc = await PayloadSDK.findByID(
            { collection: 'collateral', id, depth: 1, disableErrors: true },
            { headers: authHeaders(req) },
        );

        return doc as unknown as Collateral | null;
    },

    create: async (data: CollateralPayload) => {
        const doc = await PayloadSDK.create({
            collection: 'collateral',
            // The zod payload allows every ComponentFileType; the collection only accepts the collateral subset.
            data: data as unknown as CollateralDocument,
        });

        return doc as unknown as Collateral;
    },

    update: async (id: string, data: Partial<CollateralPayload>) => {
        const doc = await PayloadSDK.update({
            collection: 'collateral',
            id,
            data: data as unknown as Partial<CollateralDocument>,
        });

        return doc as unknown as Collateral;
    },

    delete: async (id: string) => {
        await PayloadSDK.delete({ collection: 'collateral', id });
    },
};

export { CollateralService };
